// InstallBanner.js — site-wide iOS app install prompt
// Shows on all pages for iOS visitors. Non-dismissible (WEBUX-1); the only suppression is
// arriving with a ?from_app= parameter, i.e. the visitor already has the app.
import React, { useState, useEffect } from 'react';
import './InstallBanner.css';

// Naviguesser is live on the App Store (1.0.3 released 2026-08-25). App ID 6785304344.
//
// The full canonical form, set by the user 2026-08-25. It is where Apple's 301 from the shorter
// id-only form lands anyway — verified the same day: /app/id<id>, /us/app/naviguesser/id<id> and
// even /us/app/<wrong-slug>/id<id> all return 200 and all end at exactly this URL. So the only
// behaviour change is that visitors skip one redirect hop. What the short form used to buy, and
// this gives up: this pins the /us/ storefront, so a non-US visitor is sent to the US listing
// rather than their own. The id in the path is what actually resolves — the slug is decorative —
// so an app rename would not break this link, it would just leave a stale name in it.
//
// Deliberately a literal, not a config.json field (WEBUX-1 D-1): the "no literals" rule in
// IMPLEMENTATION_GUIDELINES.MD scopes itself to Swift and Python, and config.json has no
// web-bundle copy — the React app only receives config over the network, which would leave this
// banner linkless until that fetch resolves. An App Store id also never changes.
export const APP_STORE_ID = '6785304344';
export const APP_STORE_URL = `https://apps.apple.com/us/app/naviguesser/id${APP_STORE_ID}`;

function isIOS() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function cameFromApp() {
  return new URLSearchParams(window.location.search).has('from_app');
}

function pageSubtitle() {
  const path = window.location.pathname;
  if (path.startsWith('/groups/')) return 'You were invited to a group — open in the app';
  if (path.startsWith('/options/')) return 'See this event and all your picks in the app';
  return 'Navigate Burning Man with a compass';
}

// Official Apple "Download on the App Store" badge (black variant) as inline SVG.
// Self-contained so the banner has no runtime dependency on Apple's CDN.
function AppStoreBadge() {
  return (
    <svg
      className="install-banner-badge-img"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 120 40"
      role="img"
      aria-hidden="true"
    >
      <rect width="120" height="40" rx="6" fill="#000" />
      <rect x="0.5" y="0.5" width="119" height="39" rx="5.5" fill="none" stroke="#a6a6a6" strokeWidth="0.5" />
      <path
        fill="#fff"
        d="M24.77 20.3c-.02-2.4 1.96-3.56 2.05-3.62-1.12-1.64-2.86-1.86-3.48-1.89-1.47-.15-2.89.87-3.64.87-.76 0-1.92-.85-3.16-.83-1.6.02-3.1.94-3.93 2.39-1.7 2.94-.43 7.27 1.2 9.65.81 1.16 1.78 2.47 3.04 2.42 1.23-.05 1.69-.78 3.17-.78 1.47 0 1.89.78 3.17.75 1.31-.02 2.14-1.18 2.94-2.35.95-1.35 1.34-2.67 1.36-2.74-.03-.01-2.59-.99-2.61-3.93zM22.39 13.24c.67-.81 1.12-1.94.99-3.07-.96.04-2.13.64-2.82 1.44-.61.71-1.15 1.84-1.01 2.95 1.08.08 2.18-.55 2.84-1.32z"
      />
      <text x="42" y="16" fill="#fff" fontSize="7" fontFamily="Helvetica, Arial, sans-serif">Download on the</text>
      <text x="42" y="30" fill="#fff" fontSize="15" fontWeight="600" fontFamily="Helvetica, Arial, sans-serif">App Store</text>
    </svg>
  );
}

export default function InstallBanner({ subtitle }) {
  const [visible, setVisible] = useState(false);

  // WEBUX-1: non-dismissible (user decision: "always show"). The only suppressions are
  // non-iOS and ?from_app= — the latter because the visitor already has the app.
  useEffect(() => {
    if (isIOS() && !cameFromApp()) setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <div className="install-banner" role="banner" aria-label="Install Naviguesser app">
      <span className="install-banner-icon">🧭</span>
      <div className="install-banner-text">
        <p className="install-banner-title">Naviguesser</p>
        <p className="install-banner-subtitle">
          {subtitle || pageSubtitle()}
        </p>
      </div>
      <div className="install-banner-actions">
        <a
          href={APP_STORE_URL}
          className="install-banner-badge"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store"
        >
          <AppStoreBadge />
        </a>
      </div>
    </div>
  );
}
